<template>
  <div class="IndicatorPopup PlatformMapPopup">
    <div v-if="title" class="IndicatorPopup__title">{{ title }}</div>
    <div
      class="IndicatorPopup__content"
      :class="{ 'IndicatorPopup__content--withGauge': showGauge && gaugeNumericValue != null }"
    >
      <template
        v-if="
          format === 'table' &&
          !(attributsRows && attributsRows.length > 0) &&
          !(showGauge && gaugeNumericValue != null)
        "
      >
        <table v-if="tableData && tableData.length > 0" class="IndicatorPopup__table">
          <tbody>
            <tr v-for="(item, index) in tableData" :key="index" class="IndicatorPopup__tableRow">
              <td class="IndicatorPopup__tableCell IndicatorPopup__tableCell--label">
                {{ item.label }}
              </td>
              <td class="IndicatorPopup__tableCell IndicatorPopup__tableCell--value">
                {{ item.value }}
              </td>
            </tr>
          </tbody>
        </table>
        <span v-else class="IndicatorPopup__value IndicatorPopup__value--no-data">N/A</span>
      </template>
      <template v-else>
        <div v-if="showGauge && gaugeNumericValue != null" class="IndicatorPopup__gaugeWrap">
          <p class="IndicatorPopup__gaugeLabel">{{ t('infrastructure.popup.gaugeLabel') }}</p>
          <CompletionGauge
            :value="gaugeNumericValue"
            :unit="unit"
            :decimals="decimals"
            :size="100"
          />
        </div>
        <template v-else>
          <span
            v-if="value != null && typeof value !== 'object'"
            class="IndicatorPopup__value"
          >
            {{ formatValue(value) }}
            <span v-if="unit" class="IndicatorPopup__unit">{{ unit }}</span>
          </span>
          <span v-else class="IndicatorPopup__value IndicatorPopup__value--no-data">
            {{ value == null ? 'N/A' : '' }}
          </span>
        </template>
      </template>
    </div>
    <template v-if="attributsRows && attributsRows.length > 0">
      <div class="IndicatorPopup__attributsSection">
        <table class="IndicatorPopup__table">
          <tbody>
            <tr v-for="(row, index) in attributsRows" :key="index" class="IndicatorPopup__tableRow">
              <td class="IndicatorPopup__tableCell IndicatorPopup__tableCell--label">
                {{ row.label }}
              </td>
              <td class="IndicatorPopup__tableCell IndicatorPopup__tableCell--value">
                {{ row.value }}
              </td>
            </tr>
          </tbody>
        </table>
        <p v-if="attributesCountText" class="IndicatorPopup__attributesCount" aria-live="polite">
          {{ attributesCountText }}
        </p>
      </div>
    </template>
    <p
      v-else-if="attributesCountText"
      class="IndicatorPopup__attributesCount IndicatorPopup__attributesCount--standalone"
      aria-live="polite"
    >
      {{ attributesCountText }}
    </p>
    <button
      v-if="showFicheAttributsButton && idInfrastructure != null"
      type="button"
      class="IndicatorPopup__ficheBtn"
      :aria-label="t('infrastructure.fiche.viewFicheButton')"
      @click="openFicheModal"
    >
      {{ t('infrastructure.fiche.viewFicheButton') }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { IndicatorPopupFormat } from '@/utils/indicatorNatureFormat'
import CompletionGauge from '@/views/map/components/CompletionGauge.vue'

const { t } = useI18n()

interface Props {
  title?: string
  value: number | string | boolean | null | undefined
  unit?: string
  format?: IndicatorPopupFormat
  decimals?: number
  idInfrastructure?: string | number
  showFicheAttributsButton?: boolean
  onOpenFiche?: (id: string | number) => void
  /** Number of attributes visible in the popup (table format). */
  visibleAttributesCount?: number
  /** Total number of attributes for this infrastructure (table format). */
  totalAttributesCount?: number
  /** Rows to show as "Attributs renseignés" (label/value), from feature.attributs non-null. */
  attributsRows?: Array<{ label: string; value: string }>
  /** When true and value is a number 0–100, show completion gauge instead of/in addition to raw value. */
  showGauge?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: undefined,
  value: null,
  unit: undefined,
  format: 'number',
  decimals: 2,
  idInfrastructure: undefined,
  showFicheAttributsButton: false,
  onOpenFiche: undefined,
  visibleAttributesCount: undefined,
  totalAttributesCount: undefined,
  attributsRows: undefined,
  showGauge: false,
})

const gaugeNumericValue = computed<number | null>(() => {
  if (!props.showGauge) return null
  const v = props.value
  if (v === null || v === undefined) return null
  const n = typeof v === 'number' ? v : Number(v)
  if (Number.isNaN(n)) return null
  return Math.min(100, Math.max(0, n))
})

const attributesCountText = computed(() => {
  const visible =
    props.visibleAttributesCount ??
    props.attributsRows?.length ??
    (props.format === 'table' ? (tableData.value?.length ?? 0) : 0)
  const total = props.totalAttributesCount ?? visible
  if (visible === 0 && total === 0) return ''
  return t('infrastructure.popup.attributesCount', { visible, total })
})

function openFicheModal() {
  if (props.idInfrastructure != null && props.onOpenFiche) {
    props.onOpenFiche(props.idInfrastructure)
  }
}

const tableData = computed(() => {
  if (props.format !== 'table' || !props.value) {
    return null
  }

  try {
    let jsonValue: unknown = props.value

    if (typeof props.value === 'string') {
      try {
        jsonValue = JSON.parse(props.value)
      } catch {
        return null
      }
    }

    if (typeof jsonValue === 'object' && jsonValue !== null && !Array.isArray(jsonValue)) {
      return Object.entries(jsonValue).map(([label, value]) => ({
        label,
        value: typeof value === 'number' ? value.toLocaleString() : String(value),
      }))
    }

    return null
  } catch (error) {
    console.error('Error parsing table data:', error)
    return null
  }
})

function formatValue(val: number | string | boolean | null | undefined): string {
  if (val === null || val === undefined) {
    return 'N/A'
  }

  if (typeof val === 'object') {
    return 'N/A'
  }

  if (props.format === 'boolean') {
    const boolVal =
      typeof val === 'boolean'
        ? val
        : (typeof val === 'string' && /^(1|true|oui|yes)$/i.test(val.trim())) || val === 1
    return boolVal ? t('common.yes') : t('common.no')
  }

  if (props.format === 'date') {
    try {
      const date = typeof val === 'number' ? new Date(val) : new Date(String(val))
      if (Number.isNaN(date.getTime())) return String(val)
      return date.toLocaleDateString(undefined, { dateStyle: 'medium' })
    } catch {
      return String(val)
    }
  }

  if (typeof val === 'string') {
    return val
  }

  if (props.format === 'percentage') {
    return `${Number(val).toFixed(props.decimals)}%`
  }

  if (props.format === 'number') {
    return Number(val).toFixed(props.decimals)
  }

  return String(val)
}
</script>

<style lang="scss" scoped>
@use './indicator-popup.shared.scss' as *;
@use './platform-map-popup.shared.scss' as *;
</style>
